/**
 * Each angle's sound, as a level per 100 ms frame on the set's timeline, for
 * working out who's speaking (speakingActivity.ts).
 *
 * MP4 and MOV: only the audio is read, through range requests (isoAudio.ts),
 * decoded at a low sample rate in windows of a couple of minutes
 * (audioDecode.ts), and turned into levels before the next window is read,
 * so memory stays flat on long recordings. Angles are read one after another
 * and a window's requests share a small cap, so the page never has more than
 * a few requests in flight. An hour on three cameras is roughly 170 MB of AAC.
 *
 * WebM and MKV keep no index of their sound, and browser recordings have no
 * cues, so finding each window on its own would walk the file from the start
 * every time. Such a file is walked once instead, video and all, keeping only
 * its (compressed) sound; its windows are then decoded from that, the same way.
 *
 * Timeline time t shows the angle's file at t − timelineStart + sourceIn, so
 * frame k (timeline [k·f, (k+1)·f)) is read from that stretch of the file;
 * frames before the angle starts, after it ends, or past the end of its sound
 * have no level (NaN).
 */

import { ByteSource, isAbortError, urlByteSource } from './byteSource';
import { PcmClip, StreamDecoder, readAudioClip } from './audioDecode';
import { AudioTrack, MediaReadError, PacketTable } from './isoAudio';
import { MatroskaTrack, frameBackedSource, isMatroskaTrack, isXiphTrack, readMatroskaTrack } from './matroskaAudio';
import { MediaAudioTrack, openMediaAudio, readTrackClip } from './mediaAudio';
import { AngleLevels, SPEAKING_FRAME_S, frameLevelsDb } from './speakingActivity';
import { PacketDecoder, readXiphClip } from './xiphDecode';

/** Plenty for speech loudness; small in memory. */
export const SPEAKING_SAMPLE_RATE = 8000;
/** File seconds decoded per window. */
const WINDOW_S = 120;
/** Requests in flight within a window. */
const READ_CONCURRENCY = 4;
/** Longest timeline read: the transcripts' limit, with room. */
export const MAX_SPEAKING_TIMELINE_S = 6 * 60 * 60;
/**
 * A WebM/MKV is walked whole, video and all: past this much an angle is
 * left out rather than downloaded.
 */
export const SPEAKING_MAX_SCAN_BYTES = 6 * 1024 * 1024 * 1024;
/** Of a WebM/MKV angle's progress, the share spent walking the file. */
const WALK_SHARE = 0.8;

const TOO_LARGE = 'too-large-to-walk';
const NO_SOUND_ON_TIMELINE = 'This angle has no sound on the timeline.';

export interface LevelsSource {
  key: string;
  /** Where the file is; used unless `source` is given. */
  url?: string;
  source?: ByteSource;
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs?: number | null;
}

export interface LevelsProgress {
  /** 0..1 over every angle. */
  fraction: number;
  /** The angle being read. */
  key: string;
}

export interface ReadLevelsOptions {
  signal?: AbortSignal;
  onProgress?: (progress: LevelsProgress) => void;
  decoder?: StreamDecoder;
  /** Replaces WebCodecs for Opus and Vorbis (tests). */
  packetDecoder?: PacketDecoder;
  openSource?: (url: string, signal?: AbortSignal) => Promise<ByteSource>;
  frameS?: number;
  windowS?: number;
  concurrency?: number;
}

export interface LevelsFailure {
  key: string;
  reason: string;
}

export interface SpeakingLevels {
  frameS: number;
  frames: number;
  /** The angles that could be read, every array `frames` long. */
  angles: AngleLevels[];
  failures: LevelsFailure[];
}

/** Why an angle's sound couldn't be read, in words for the creator. */
export const describeLevelsFailure = (error: unknown): string => {
  if (error instanceof MediaReadError) {
    if (error.message === NO_SOUND_ON_TIMELINE) return NO_SOUND_ON_TIMELINE;
    if (error.message === TOO_LARGE || /downloading too much/i.test(error.message)) {
      return 'This angle’s WebM or MKV file is too large to listen to in the browser.';
    }
    switch (error.code) {
      case 'not-mp4':
        return 'PithPlay can only listen to MP4, MOV, WebM and MKV files.';
      case 'no-audio':
        return 'This angle has no sound.';
      case 'unsupported-audio':
        return 'This angle’s sound can’t be read in this browser.';
      default:
        return 'This angle’s file couldn’t be read.';
    }
  }
  const message = error instanceof Error ? error.message : '';
  if (/expired|not allowed/i.test(message)) return 'The link to this video has expired. Reload the page and try again.';
  if (/decode/i.test(message)) return 'This angle’s sound couldn’t be decoded in this browser.';
  return 'This angle couldn’t be read. Check your connection and try again.';
};

interface Plan {
  input: LevelsSource;
  source: ByteSource;
  track: MediaAudioTrack;
  /** Timeline frames [first, end) the angle covers. */
  first: number;
  end: number;
  /** File seconds shown at timeline 0. */
  shiftS: number;
}

const abortError = () => {
  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';
  return error;
};

/** A byte source that reports how much of an expected download it has read. */
const reportingSource = (source: ByteSource, expectedBytes: number, report: (fraction: number) => void): ByteSource => {
  let read = 0;
  return {
    size: source.size,
    async read(offset, length, signal) {
      const bytes = await source.read(offset, length, signal);
      read += bytes.length;
      if (expectedBytes > 0) report(Math.min(1, read / expectedBytes));
      return bytes;
    },
  };
};

/**
 * Levels for every angle on the timeline. An angle that can't be read is
 * reported in `failures` and left out; the others carry on.
 */
export const readSpeakingLevels = async (sources: LevelsSource[], options: ReadLevelsOptions = {}): Promise<SpeakingLevels> => {
  const { signal, onProgress, decoder, packetDecoder } = options;
  const frameS = options.frameS ?? SPEAKING_FRAME_S;
  const windowFrames = Math.max(1, Math.round((options.windowS ?? WINDOW_S) / frameS));
  const concurrency = options.concurrency ?? READ_CONCURRENCY;
  const maxFrames = Math.floor(MAX_SPEAKING_TIMELINE_S / frameS);
  const openSource = options.openSource ?? ((url: string, sig?: AbortSignal) => urlByteSource(url, { signal: sig }));
  const failures: LevelsFailure[] = [];

  // Open every angle first: the index says how much there is to read.
  const plans: Plan[] = [];
  for (let i = 0; i < sources.length; i += 1) {
    const input = sources[i];
    if (signal?.aborted) throw abortError();
    onProgress?.({ fraction: 0, key: input.key });
    try {
      const opened = await openMediaAudio(input.source ?? (await openSource(input.url ?? '', signal)), { signal });
      const { source, track } = opened;
      // A WebM/MKV that stores no length says only how much of it has been
      // read so far; its real end is found when it is walked.
      const fileS = isMatroskaTrack(track) && track.matroska.durationSeconds === null ? Infinity : track.durationSeconds;
      const inS = Math.max(0, Number(input.sourceInMs) || 0) / 1000;
      const out = Number(input.sourceOutMs);
      const outS = Math.min(fileS, Number.isFinite(out) && out / 1000 > inS ? out / 1000 : Infinity);
      const startS = Math.max(0, Number(input.timelineStartMs) || 0) / 1000;
      const first = Math.max(0, Math.ceil(startS / frameS - 1e-6));
      const end = Math.min(maxFrames, Math.floor((startS + Math.max(0, outS - inS)) / frameS + 1e-6));
      if (end <= first) {
        failures.push({ key: input.key, reason: NO_SOUND_ON_TIMELINE });
        continue;
      }
      plans.push({ input, source, track, first, end, shiftS: inS - startS });
    } catch (error) {
      if (isAbortError(error)) throw error;
      failures.push({ key: input.key, reason: describeLevelsFailure(error) });
    }
  }

  const totalFrames = plans.reduce((sum, plan) => sum + (plan.end - plan.first), 0);
  let doneFrames = 0;
  let reported = 0;
  /** `done` frames of every angle read so far; never goes back. */
  const report = (key: string, done: number) => {
    reported = Math.max(reported, totalFrames > 0 ? Math.min(1, done / totalFrames) : 1);
    onProgress?.({ fraction: reported, key });
  };

  /**
   * A WebM/MKV angle: one walk over the file collects its sound, then each
   * window decodes from memory. Returns where its frames end, which for a
   * file with no stored length is only known after the walk.
   */
  const walkMatroska = async (plan: Plan, track: MatroskaTrack, before: number) => {
    const planFrames = plan.end - plan.first;
    const fromS = plan.first * frameS + plan.shiftS;
    const toS = plan.end * frameS + plan.shiftS;
    const layout = track.matroska;
    // Without cues the walk starts at the first cluster, whatever fromS is.
    const walkFromS = layout.cues || layout.cuesAt !== null ? fromS : 0;
    const lengthS = layout.durationSeconds;
    const expectedBytes = lengthS && lengthS > 0
      ? (plan.source.size * Math.max(0, Math.min(toS, lengthS) - walkFromS)) / lengthS
      : plan.source.size;
    if (expectedBytes > SPEAKING_MAX_SCAN_BYTES) throw new MediaReadError('unsupported-audio', TOO_LARGE);
    const found = await readMatroskaTrack(
      reportingSource(plan.source, expectedBytes, (fraction) => report(plan.input.key, before + fraction * WALK_SHARE * planFrames)),
      { fromS, toS, layout, signal, maxScanBytes: SPEAKING_MAX_SCAN_BYTES, collectFrames: true }
    );
    const reader = found.frames ? frameBackedSource(plan.source, found.table as PacketTable, found.frames) : plan.source;
    const soundEnd = Math.floor((found.durationSeconds - plan.shiftS) / frameS + 1e-6);
    const end = Math.max(plan.first, Math.min(plan.end, soundEnd));
    const clipOf = (startS: number, endS: number, onClipProgress: (fraction: number) => void): Promise<PcmClip> => {
      const clipOptions = { signal, decoder, packetDecoder, concurrency, onProgress: onClipProgress };
      return isXiphTrack(found)
        ? readXiphClip(reader, found, startS, endS, SPEAKING_SAMPLE_RATE, clipOptions)
        : readAudioClip(reader, found as AudioTrack, startS, endS, SPEAKING_SAMPLE_RATE, clipOptions);
    };
    return { end, clipOf };
  };

  const read: Array<{ key: string; first: number; values: Float32Array }> = [];
  for (let p = 0; p < plans.length; p += 1) {
    const plan = plans[p];
    const { input, source, track } = plan;
    const planFrames = plan.end - plan.first;
    const before = doneFrames;
    try {
      let end = plan.end;
      // Progress, in frames of this angle, already spent before its windows.
      let lead = 0;
      let clipOf = (startS: number, endS: number, onClipProgress: (fraction: number) => void): Promise<PcmClip> =>
        readTrackClip(source, track, startS, endS, SPEAKING_SAMPLE_RATE, {
          signal,
          decoder,
          packetDecoder,
          concurrency,
          maxScanBytes: SPEAKING_MAX_SCAN_BYTES,
          onProgress: onClipProgress,
        });
      if (isMatroskaTrack(track)) {
        const walked = await walkMatroska(plan, track, before);
        end = walked.end;
        clipOf = walked.clipOf;
        lead = WALK_SHARE * planFrames;
        if (end <= plan.first) throw new MediaReadError('no-audio', NO_SOUND_ON_TIMELINE);
      }
      // Window progress, scaled into what is left of this angle's share.
      const scale = end > plan.first ? (planFrames - lead) / (end - plan.first) : 0;
      const values = new Float32Array(end - plan.first).fill(NaN);
      for (let from = plan.first; from < end; from += windowFrames) {
        if (signal?.aborted) throw abortError();
        const to = Math.min(end, from + windowFrames);
        const clip = await clipOf(from * frameS + plan.shiftS, to * frameS + plan.shiftS, (fraction) =>
          report(input.key, before + lead + ((from - plan.first) + fraction * (to - from)) * scale)
        );
        const levels = frameLevelsDb(clip, frameS);
        values.set(levels.subarray(0, Math.min(levels.length, to - from)), from - plan.first);
        doneFrames = before + lead + (to - plan.first) * scale;
        report(input.key, doneFrames);
      }
      read.push({ key: input.key, first: plan.first, values });
    } catch (error) {
      if (isAbortError(error)) throw error;
      failures.push({ key: input.key, reason: describeLevelsFailure(error) });
    }
    doneFrames = before + planFrames;
    report(input.key, doneFrames);
  }

  let frames = 0;
  read.forEach((entry) => {
    frames = Math.max(frames, entry.first + entry.values.length);
  });
  const angles = read.map((entry) => {
    const levels = new Float32Array(frames).fill(NaN);
    levels.set(entry.values, entry.first);
    return { key: entry.key, levels };
  });
  report(sources[sources.length - 1]?.key ?? '', totalFrames);
  return { frameS, frames, angles, failures };
};
